import * as styles from "./ContractEditorBody.css";

import { clsx } from "clsx";
import { useAtomValue, useSetAtom } from "jotai";
import { type JSX, useState } from "react";

import { Badge } from "@frontend/components/atoms/Badge";
import { Button } from "@frontend/components/atoms/Button";
import { Icon } from "@frontend/components/atoms/Icon";
import { Spinner } from "@frontend/components/atoms/Spinner";
import {
  Tabs,
  TabsContent,
  TabsIndicator,
  TabsList,
  TabsPanel,
  TabsTab,
  stickyTabsBar,
} from "@frontend/components/atoms/Tabs";
import { useArrayField } from "@frontend/hooks/form";
import { Trans, useLingui } from "@lingui/react/macro";

import {
  addAmendmentAtom,
  contractEditorAtom,
  contractForm,
} from "../stores/editor";
import { AmendmentList } from "./AmendmentList";
import { ContractFields } from "./ContractFields";

export type ContractEditorBodyProps = {
  loading: boolean;
  error: string | null;
  conflict: boolean;
  stickyTabs?: boolean;
};

export function ContractEditorBody({
  loading,
  error,
  conflict,
  stickyTabs = false,
}: ContractEditorBodyProps): JSX.Element {
  const { t } = useLingui();

  const willTerminateCurrent =
    useAtomValue(contractEditorAtom).willTerminateCurrent;

  const amendments = useArrayField(contractForm.fields.amendments);
  const addAmendment = useSetAtom(addAmendmentAtom);

  const [tab, setTab] = useState("main");

  function add() {
    addAmendment();
    setTab("amendments");
  }

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spinner aria-label={t`Loading`} />
      </div>
    );
  }

  return (
    <div className={styles.body}>
      {error ? (
        <p className={styles.alert} role="alert">
          {conflict ? (
            <Trans>
              Someone else saved this contract while it was open. Reopen it to
              see their version, then make your change again.
            </Trans>
          ) : (
            error
          )}
        </p>
      ) : null}

      {willTerminateCurrent ? (
        <p className={styles.notice}>
          <Trans>
            Saving this contract ends the one that is running, the day before it
            starts.
          </Trans>
        </p>
      ) : null}

      <Tabs className={styles.tabs} value={tab} onValueChange={setTab}>
        <div className={clsx(styles.tabsHeader, stickyTabs && stickyTabsBar)}>
          <TabsList>
            <TabsTab value="main" icon="FileSignature">
              <Trans>Contract</Trans>
            </TabsTab>

            <TabsTab value="amendments" icon="ListPlus">
              <Trans>Amendments</Trans>

              {0 === amendments.count ? null : (
                <Badge variant="neutral" size="sm" dot={false}>
                  {amendments.count}
                </Badge>
              )}
            </TabsTab>

            <TabsIndicator />
          </TabsList>

          <Button
            variant="ghost"
            size="sm"
            leftSection={<Icon name="Plus" size="sm" />}
            onClick={add}
          >
            {t`Add an amendment`}
          </Button>
        </div>

        <TabsContent className={styles.tabsContent}>
          <TabsPanel value="main">
            <ContractFields />
          </TabsPanel>

          <TabsPanel value="amendments" className={styles.amendmentsPanel}>
            <AmendmentList />
          </TabsPanel>
        </TabsContent>
      </Tabs>
    </div>
  );
}
